/**
 * Weekly menu route
 * View the weekly menu set by organizers
 */

import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import type { RootSearchParams } from '@/routes/__root';
import { useTranslation } from 'react-i18next';
import { useWeek } from '@/contexts/WeekContext';
import { useWeekMenu } from '@/hooks/useMenus';
import { MenuWeekView } from '@/components/menu/MenuWeekView';
import { WeekNavigator } from '@/components/availability/WeekNavigator';
import { requireAuth } from '@/lib/auth/routeGuards';

export const Route = createFileRoute('/menu/')({
  beforeLoad: requireAuth,
  component: MenuComponent,
});

function MenuComponent() {
  const { t } = useTranslation();
  // Get user from route context - guaranteed non-null by requireAuth
  const { user } = Route.useRouteContext();

  // Get current search params to preserve week when navigating
  const rootSearch = useSearch({ from: '__root__' }) as RootSearchParams;

  // Use global week context
  const { weekStart, weekStartDate, goToPreviousWeek, goToNextWeek, goToToday } = useWeek();

  // Fetch weekly menu
  const { data, isLoading, error } = useWeekMenu({ startDate: weekStartDate });

  const weekMenus = data?.menus || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('menu.weeklyMenu')}</h1>
            <p className="mt-2 text-sm text-gray-600">
              {t('menu.viewMenuDescription')}
            </p>
          </div>
          {user.isOrganizer && (
            <Link
              to="/menu/edit"
              search={rootSearch}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-700 rounded-lg hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              {t('menu.editMenuButton')}
            </Link>
          )}
        </div>

        {/* Error state */}
        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-6" role="alert">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {t('menu.errorLoadingMenuTitle')}
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  {error instanceof Error
                    ? error.message
                    : t('menu.errorLoadingMenuMessage')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Week navigator */}
        <WeekNavigator
          currentWeekStart={weekStart}
          onPreviousWeek={goToPreviousWeek}
          onNextWeek={goToNextWeek}
          onToday={goToToday}
        />

        {/* Week menu view */}
        <MenuWeekView weekData={weekMenus} isLoading={isLoading} />

      </div>
    </div>
  );
}
